import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VehicleCard from "../components/VehicleCard";
import ContentBlockRenderer from "../components/ContentBlockRenderer";
import { vehicles } from "../data/vehicles";
import { ArrowRight } from "lucide-react";
import { usePage, useSeo, cmsMediaUrl } from "../lib/cms";

const featured = vehicles.slice(0, 3);

const HomePage = () => {
  const { page, loading } = usePage("home");

  useSeo({
    title: page?.seo_title || page?.title || "Maison Vellore",
    description: page?.seo_description,
  });

  const heroTitle =
    page?.hero_title || "Een discrete collectie zeldzame automobielen.";
  const heroSubtitle = page?.hero_subtitle || "Maison Vellore — Est. 2011";
  const introText = page?.intro_text;
  const ctaText = page?.cta_text || "Bekijk het aanbod";
  const ctaLink = page?.cta_link || "/aanbod";
  const heroImage = cmsMediaUrl(page?.hero_image);
  const fallbackHero =
    "https://images.unsplash.com/photo-1776102669015-21d5f6c0cdf8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBkYXJrJTIwc2hvd3Jvb218ZW58MHx8fHwxNzc4MDUzNjAyfDA&ixlib=rb-4.1.0&q=85";

  return (
    <div data-testid="home-page" className="min-h-screen bg-ink-950">
      <Header />
      <main>
        <section
          data-testid="home-hero"
          className="relative overflow-hidden min-h-[88vh] flex items-end"
        >
          <div className="absolute inset-0">
            <img
              src={heroImage || fallbackHero}
              alt={page?.hero_image?.alt_text || "Maison Vellore"}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/55 to-ink-950/20" />
            <div className="absolute inset-0 noise-overlay" />
          </div>

          <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 w-full pb-20 md:pb-28">
            <div className="flex items-center gap-3 mb-8 animate-fade-in">
              <span className="h-px w-10 bg-champagne" />
              <span
                data-testid="home-hero-subtitle"
                className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body"
              >
                {heroSubtitle}
              </span>
            </div>
            <h1
              data-testid="home-hero-title"
              className="font-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] font-light leading-[0.9] tracking-tight animate-fade-up max-w-5xl"
            >
              {heroTitle}
            </h1>
            {introText && (
              <p className="mt-8 max-w-xl text-base text-zinc-300 font-body leading-relaxed animate-fade-up">
                {introText}
              </p>
            )}
            <div
              className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
              style={{ animationDelay: "200ms" }}
            >
              <Link
                to={ctaLink}
                data-testid="home-cta-aanbod"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
              >
                {ctaText}
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-white/20 text-white hover:border-champagne hover:text-champagne transition-colors text-[11px] tracking-[0.3em] uppercase font-body"
              >
                Privé bezichtiging
              </Link>
            </div>
          </div>
        </section>

        {/* Featured cars (still static demo data) */}
        <section
          data-testid="home-featured"
          className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body">
                  Uitgelicht
                </span>
                <span className="h-px w-10 bg-champagne" />
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] max-w-2xl">
                Een selectie uit onze huidige collectie
              </h2>
            </div>
            <Link
              to="/aanbod"
              data-testid="home-view-all"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-champagne hover:text-white transition-colors font-body"
            >
              Volledige collectie
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            {featured.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        </section>

        {/* CMS-driven blocks */}
        {!loading &&
          page?.blocks?.map((block) => (
            <ContentBlockRenderer key={block.id} block={block} />
          ))}
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
